"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { BUTTON_SMALL, LABEL, WIDGET_H2 } from "@/components/tool-kit/styles";
import { QuantityField } from "@/components/tools/physics-waves/quantity-field";
import { BandPickers } from "@/components/tools/physics-waves/resistor-inputs";
import { ResistorOutcome } from "@/components/tools/physics-waves/resistor-view";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  type NumberFormatter,
  withSiPrefix,
} from "@/lib/tool-kit/physics-waves/format";
import {
  type BandCount,
  type BandSet,
  bandSetFrom,
  bandsForStandard,
  colorDef,
  type EncodeError,
  encodeForForm,
  nearestStandard,
  type PaintedBands,
  SERIES_IDS,
  SERIES_TOLERANCE,
  type SeriesId,
  TEMPCO_COLORS,
} from "@/lib/tool-kit/physics-waves/resistor-code";
import {
  findUnit,
  prefixUnits,
  toBase,
} from "@/lib/tool-kit/physics-waves/units";

/** English strings. A translated content file overrides these through `ui`. */
export const RESISTOR_COLOR_CODE_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to do?",
  modes: {
    decode: "Read the bands",
    encode: "Find the bands for a value",
    nearest: "Nearest standard value",
  },
  bandCountLabel: "Number of bands",
  bandCounts: { "4": "4 bands", "5": "5 bands", "6": "6 bands" },
  tryLabel: "Try an example",
  presets: {
    kilo47: "4.7 kΩ ±5% (4 bands)",
    ten: "10 kΩ ±1% (5 bands)",
    precision: "100 kΩ ±1%, 50 ppm/K (6 bands)",
    small: "0.47 Ω ±10% (4 bands)",
  },
  colorNames: {
    black: "Black",
    brown: "Brown",
    red: "Red",
    orange: "Orange",
    yellow: "Yellow",
    green: "Green",
    blue: "Blue",
    violet: "Violet",
    grey: "Grey",
    white: "White",
    gold: "Gold",
    silver: "Silver",
  },
  noBand: "No band",
  noBandOption: "No tolerance band (±20%)",
  bandNames: {
    digit1: "1st digit",
    digit2: "2nd digit",
    digit3: "3rd digit",
    multiplier: "Multiplier",
    tolerance: "Tolerance",
    tempco: "Temperature coefficient",
  },
  digitOption: "{color} ({digit})",
  multiplierOption: "{color} (× {factor})",
  toleranceOption: "{color} (±{percent}%)",
  tempcoOption: "{color} ({ppm} ppm/K)",
  digitMeaning: "Digit {digit}",
  multiplierMeaning: "× {factor}",
  toleranceMeaning: "±{percent}%",
  tempcoMeaning: "{ppm} ppm/K",
  valueLabel: "Resistance",
  unitFor: "Unit for resistance",
  toleranceLabel: "Tolerance",
  tempcoLabel: "Temperature coefficient (6th band)",
  seriesLabel: "Preferred series",
  seriesOption: "{series} — usually ±{percent}%",
  sigFigs: "Round to this many significant figures",
  results: "Results",
  resistance: "Resistance",
  tolerance: "Tolerance",
  range: "Actual value lies between",
  tempco: "Temperature coefficient",
  series: "Standard series",
  noSeries: "Not a standard value",
  working: "The working",
  workingLine: "{digits} × {factor} = {ohms}",
  bandsHeading: "Band by band",
  bandColumn: "Band",
  colorColumn: "Colour",
  meaningColumn: "Means",
  figureLabel: "Resistor with bands {colors}",
  figureDescription:
    "A resistor body with these colour bands, left to right: {colors}.",
  nearestValue: "Nearest {series} value",
  nearestError: "Difference from your value",
  nearestBelow: "Next value below",
  nearestAbove: "Next value above",
  nearestNote:
    "The bands drawn are for the nearest value, with the tolerance usually paired with {series} (±{percent}%).",
  enterValue: "Enter a resistance above zero to see the bands.",
  decodeNote:
    "Read from the end that has the tolerance band (gold, silver or the widely spaced one). Gold and silver never appear as digits.",
  errors: {
    "not-positive": "Enter a resistance above zero.",
    "too-many-digits":
      "That value needs more significant digits than this many bands can carry. Use 5 bands (three digits), or round it to a standard value with “Nearest standard value”.",
    "out-of-range":
      "The multiplier bands cover × 0.01 to × 10⁹, so this value is outside what the colour code can write.",
  },
} as const;
type Strings = typeof RESISTOR_COLOR_CODE_CALCULATOR_STRINGS;
type Mode = keyof Strings["modes"];
type CountKey = keyof Strings["bandCounts"];
type Nearest = ReturnType<typeof nearestStandard>;

const RESISTANCE_UNITS = prefixUnits("Ω", ["", "k", "M", "G"]);
const SIG_CHOICES = ["3", "4", "5"] as const;
const TOLERANCE_CHOICES = [1, 2, 5, 10, 20, 0.5, 0.25, 0.1, 0.05] as const;

const PRESET_DATA: Record<
  keyof Strings["presets"],
  { count: CountKey; bands: PaintedBands }
> = {
  kilo47: {
    count: "4",
    bands: {
      d1: "yellow",
      d2: "violet",
      d3: "black",
      mult: "red",
      tol: "gold",
      tc: "brown",
    },
  },
  ten: {
    count: "5",
    bands: {
      d1: "brown",
      d2: "black",
      d3: "black",
      mult: "red",
      tol: "brown",
      tc: "brown",
    },
  },
  precision: {
    count: "6",
    bands: {
      d1: "brown",
      d2: "black",
      d3: "black",
      mult: "orange",
      tol: "brown",
      tc: "red",
    },
  },
  small: {
    count: "4",
    bands: {
      d1: "yellow",
      d2: "violet",
      d3: "black",
      mult: "silver",
      tol: "silver",
      tc: "brown",
    },
  },
};

interface Resolved {
  bands: BandSet | null;
  error: EncodeError | null;
  nearest: Nearest;
}

interface Inputs {
  mode: Mode;
  count: BandCount;
  painted: PaintedBands;
  ohms: number | null;
  tolerance: number;
  tempco: number;
  series: SeriesId;
}

/** The bands to draw for the current mode, or the reason there are none. */
function resolve(inputs: Inputs): Resolved {
  if (inputs.mode === "decode") {
    return {
      bands: bandSetFrom(inputs.painted, inputs.count),
      error: null,
      nearest: null,
    };
  }
  const ohms = inputs.ohms ?? 0;
  if (inputs.mode === "encode") {
    const result = encodeForForm(
      ohms,
      inputs.count,
      inputs.tolerance,
      inputs.tempco
    );
    return {
      bands: result.ok ? result.bands : null,
      error: result.ok ? null : result.error,
      nearest: null,
    };
  }
  const nearest = nearestStandard(ohms, inputs.series);
  if (nearest === null) {
    return { bands: null, error: "not-positive", nearest };
  }
  const result = bandsForStandard(nearest.nearest, inputs.series);
  return {
    bands: result.ok ? result.bands : null,
    error: result.ok ? null : result.error,
    nearest,
  };
}

function NearestSummary({
  nearest,
  series,
  strings,
  fmt,
  sig,
}: {
  nearest: NonNullable<Nearest>;
  series: SeriesId;
  strings: Strings;
  fmt: NumberFormatter;
  sig: number;
}) {
  const ohm = (value: number) => withSiPrefix(fmt, value, "Ω", sig);
  const sign = nearest.errorPercent > 0 ? "+" : "";
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={t(strings.nearestValue, { series })}
          value={ohm(nearest.nearest)}
        />
        <StatCard
          label={strings.nearestError}
          value={`${sign}${fmt(nearest.errorPercent, { maximumFractionDigits: 2 })}%`}
        />
        <StatCard label={strings.nearestBelow} value={ohm(nearest.below)} />
        <StatCard label={strings.nearestAbove} value={ohm(nearest.above)} />
      </StatGrid>
      <p className="text-muted-foreground text-xs">
        {t(strings.nearestNote, {
          series,
          percent: fmt(SERIES_TOLERANCE[series]),
        })}
      </p>
    </>
  );
}

export default function ResistorColorCodeCalculatorWidget() {
  const strings = useToolUi(RESISTOR_COLOR_CODE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("decode");
  const [countKey, setCountKey] = useState<CountKey>("4");
  const [painted, setPainted] = useState<PaintedBands>(
    PRESET_DATA.kilo47.bands
  );
  const [valueText, setValueText] = useState("4.7");
  const [unit, setUnit] = useState("kΩ");
  const [tolerance, setTolerance] = useState("5");
  const [tempco, setTempco] = useState("100");
  const [series, setSeries] = useState<SeriesId>("E24");
  const [sigText, setSigText] = useState<(typeof SIG_CHOICES)[number]>("4");
  const count = Number(countKey) as BandCount;
  const parsed = parseNumberInput(valueText);
  const ohms =
    parsed === null ? null : toBase(parsed, findUnit(RESISTANCE_UNITS, unit));
  const missing = mode !== "decode" && parsed === null;
  const resolved = resolve({
    mode,
    count,
    painted,
    ohms,
    tolerance: Number(tolerance),
    tempco: Number(tempco),
    series,
  });

  const applyPreset = (key: keyof Strings["presets"]) => {
    setCountKey(PRESET_DATA[key].count);
    setPainted(PRESET_DATA[key].bands);
  };

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="rcc-mode"
        onChange={setMode}
        options={(Object.keys(strings.modes) as Mode[]).map((value) => ({
          value,
          label: strings.modes[value],
        }))}
        value={mode}
      />
      {mode === "nearest" ? null : (
        <PillGroup
          label={strings.bandCountLabel}
          name="rcc-count"
          onChange={setCountKey}
          options={(Object.keys(strings.bandCounts) as CountKey[]).map(
            (value) => ({
              value,
              label: strings.bandCounts[value],
            })
          )}
          value={countKey}
        />
      )}
      {mode === "decode" ? (
        <>
          <BandPickers
            count={count}
            fmt={fmt}
            onChange={(patch) =>
              setPainted((current) => ({ ...current, ...patch }))
            }
            painted={painted}
            strings={strings}
          />
          <div className="flex flex-wrap items-center gap-2">
            <span className={LABEL}>{strings.tryLabel}</span>
            {(Object.keys(PRESET_DATA) as (keyof Strings["presets"])[]).map(
              (key) => (
                <button
                  className={BUTTON_SMALL}
                  key={key}
                  onClick={() => applyPreset(key)}
                  type="button"
                >
                  {strings.presets[key]}
                </button>
              )
            )}
          </div>
          <p className="text-muted-foreground text-xs">{strings.decodeNote}</p>
        </>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          <QuantityField
            id="rcc-value"
            label={strings.valueLabel}
            onChange={setValueText}
            onUnitChange={setUnit}
            unit={unit}
            unitLabel={strings.unitFor}
            units={RESISTANCE_UNITS}
            value={valueText}
          />
          {mode === "encode" ? (
            <SelectField
              id="rcc-tolerance"
              label={strings.toleranceLabel}
              onChange={setTolerance}
              options={TOLERANCE_CHOICES.filter(
                (p) => count === 4 || p !== 20
              ).map((p) => ({
                value: String(p),
                label: t(strings.toleranceMeaning, { percent: fmt(p) }),
              }))}
              value={count !== 4 && tolerance === "20" ? "5" : tolerance}
            />
          ) : (
            <SelectField
              id="rcc-series"
              label={strings.seriesLabel}
              onChange={setSeries}
              options={SERIES_IDS.map((id) => ({
                value: id,
                label: t(strings.seriesOption, {
                  series: id,
                  percent: fmt(SERIES_TOLERANCE[id]),
                }),
              }))}
              value={series}
            />
          )}
          {mode === "encode" && count === 6 ? (
            <SelectField
              id="rcc-tempco"
              label={strings.tempcoLabel}
              onChange={setTempco}
              options={TEMPCO_COLORS.map((id) => ({
                value: String(colorDef(id).tempco ?? 0),
                label: t(strings.tempcoMeaning, {
                  ppm: fmt(colorDef(id).tempco ?? 0),
                }),
              }))}
              value={tempco}
            />
          ) : null}
        </div>
      )}
      <div className="max-w-xs">
        <SelectField
          id="rcc-sig"
          label={strings.sigFigs}
          onChange={setSigText}
          options={SIG_CHOICES.map((value) => ({ value, label: value }))}
          value={sigText}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {missing ? <Notice>{strings.enterValue}</Notice> : null}
        {!missing && resolved.error !== null ? (
          <ErrorNotice>{strings.errors[resolved.error]}</ErrorNotice>
        ) : null}
        {!missing && resolved.nearest !== null ? (
          <NearestSummary
            fmt={fmt}
            nearest={resolved.nearest}
            series={series}
            sig={Number(sigText)}
            strings={strings}
          />
        ) : null}
        {!missing && resolved.bands !== null ? (
          <ResistorOutcome
            bands={resolved.bands}
            fmt={fmt}
            sig={Number(sigText)}
            strings={strings}
          />
        ) : null}
      </section>
    </>
  );
}
